import { useEffect, useState, useSyncExternalStore } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
function subscribeNetwork(listener: () => void) {
  window.addEventListener('online', listener);
  window.addEventListener('offline', listener);
  return () => {
    window.removeEventListener('online', listener);
    window.removeEventListener('offline', listener);
  };
}
export function PwaRuntime() {
  const online = useSyncExternalStore(
    subscribeNetwork,
    () => navigator.onLine,
    () => true,
  );
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({ immediate: true });
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const check = () => {
      if (navigator.onLine && document.visibilityState === 'visible') {
        void navigator.serviceWorker
          .getRegistration()
          .then((registration) => registration?.update())
          .catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    const interval = window.setInterval(check, 60 * 60 * 1000);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', check);
    };
  }, []);
  return (
    <>
      {!online && (
        <div
          role="status"
          className="fixed inset-x-3 top-20 z-30 rounded-xl border border-[var(--border)] bg-[var(--surface-1)] px-4 py-3 text-sm text-[var(--text)] shadow-lg"
        >
          You’re offline. Reconnect before saving changes; records on screen may be out of date.
        </div>
      )}
      {needRefresh && !dismissed && (
        <section
          aria-label="App update available"
          className="fixed inset-x-4 bottom-20 z-30 space-y-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] p-4 text-[var(--text)] shadow-xl md:left-auto md:bottom-5 md:w-96"
        >
          <h2 className="font-semibold">A new version of FinBob is ready</h2>
          <p className="text-sm text-[var(--text-muted)]">
            Save your changes in every open FinBob window before updating. Open windows may reload.
          </p>
          {error && (
            <p role="alert" className="text-sm">
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <button
              className="pwa-button pwa-primary"
              disabled={updating || !online}
              onClick={async () => {
                setUpdating(true);
                setError('');
                try {
                  await updateServiceWorker(true);
                } catch {
                  setError('The update could not be applied. Please try again.');
                  setUpdating(false);
                }
              }}
            >
              Update & reload
            </button>
            <button className="pwa-button" disabled={updating} onClick={() => setDismissed(true)}>
              Later
            </button>
          </div>
        </section>
      )}
    </>
  );
}
